import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, CheckCircle2, XCircle, DollarSign, Search,
  Loader2, Gavel, Ban, Eye, Scale, FileText,
} from "lucide-react";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { ColumnDef, PaginationState } from "@tanstack/react-table";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-green-50 text-green-700 border-green-200",
  waived: "bg-blue-50 text-blue-700 border-blue-200",
};

const TYPE_LABELS: Record<string, string> = {
  loan_late: "Loan Late Repayment",
  contribution_late: "Contribution Late Payment",
};

export default function PenaltyManagement() {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "appliedDate", desc: true }]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 15 });
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedPenalty, setSelectedPenalty] = useState<any>(null);
  const [waiveReason, setWaiveReason] = useState("");
  const [dialogMode, setDialogMode] = useState<"waive" | "pay" | "view">("view");

  const { data: summary, isLoading: summaryLoading } = useQuery(
    ["penalty-admin-summary"],
    async () => {
      const { data } = await api.get("/penalties/admin/summary");
      return data.data;
    }
  );

  const { data: penaltiesData, isLoading: penaltiesLoading } = useQuery(
    ["penalties-admin", typeFilter, statusFilter],
    async () => {
      const params: any = { penaltyType: typeFilter, status: statusFilter };
      const { data } = await api.get("/penalties/admin", { params });
      return data.data || [];
    }
  );

  const processPenaltiesMutation = useMutation(
    async () => {
      const { data } = await api.post("/penalties/process");
      return data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["penalties-admin"]);
        queryClient.invalidateQueries(["penalty-admin-summary"]);
        toast.success(data.message || "Penalties processed successfully");
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to process penalties");
      },
    }
  );

  const waiveMutation = useMutation(
    async ({ id, reason }: { id: number; reason: string }) => {
      const { data } = await api.patch(`/penalties/${id}/waive`, { reason });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["penalties-admin"]);
        queryClient.invalidateQueries(["penalty-admin-summary"]);
        toast.success("Penalty waived successfully");
        setSelectedPenalty(null);
        setWaiveReason("");
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to waive penalty");
      },
    }
  );

  const payMutation = useMutation(
    async (id: number) => {
      const { data } = await api.patch(`/penalties/${id}/pay`);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["penalties-admin"]);
        queryClient.invalidateQueries(["penalty-admin-summary"]);
        toast.success("Penalty marked as paid");
        setSelectedPenalty(null);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to mark penalty as paid");
      },
    }
  );

  const penalties = penaltiesData || [];
  const filteredData = useMemo(() => {
    let result = [...penalties];
    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter((p: any) =>
        p.member?.fullNames?.toLowerCase().includes(q) ||
        p.reason?.toLowerCase().includes(q) ||
        p.period?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [penalties, searchText]);

  const pageCount = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const paginatedData = useMemo(() =>
    filteredData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [filteredData, pageIndex, pageSize]
  );

  const handleSearch = useCallback((val: string) => {
    setSearchText(val);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const openDialog = (penalty: any, mode: "view" | "waive" | "pay") => {
    setSelectedPenalty(penalty);
    setDialogMode(mode);
    setWaiveReason("");
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "appliedDate",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => {
        const d = new Date(row.getValue("appliedDate"));
        return <div className="whitespace-nowrap text-sm">{d.toLocaleDateString()}</div>;
      },
    },
    {
      accessorKey: "member",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Member" />,
      cell: ({ row }) => {
        const member = row.original.member;
        return (
          <div className="font-medium text-sm">
            {member?.fullNames || `Member #${row.original.memberId}`}
          </div>
        );
      },
    },
    {
      accessorKey: "penaltyType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => {
        const type = row.getValue("penaltyType") as string;
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-xs">
            {TYPE_LABELS[type] || type}
          </Badge>
        );
      },
    },
    {
      accessorKey: "amount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
      cell: ({ row }) => (
        <div className="font-semibold text-sm">
          {Number(row.getValue("amount")).toLocaleString()} FRW
        </div>
      ),
    },
    {
      accessorKey: "period",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Period" />,
      cell: ({ row }) => <div className="text-sm text-gray-600">{row.getValue("period") || "-"}</div>,
    },
    {
      accessorKey: "reason",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Reason" />,
      cell: ({ row }) => (
        <div className="text-sm text-gray-600 max-w-[200px] truncate" title={row.getValue("reason")}>
          {row.getValue("reason")}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        return (
          <Badge variant="outline" className={`${STATUS_STYLES[status] || ""} text-xs capitalize`}>
            {status}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Actions" />,
      cell: ({ row }) => {
        const penalty = row.original;
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => openDialog(penalty, "view")}
              title="View details"
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
            {penalty.status === "pending" && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-green-600"
                  onClick={() => openDialog(penalty, "pay")}
                  title="Mark as paid"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-blue-600"
                  onClick={() => openDialog(penalty, "waive")}
                  title="Waive penalty"
                >
                  <Ban className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const facets = [
    {
      name: "status",
      title: "Status",
      type: "checkbox",
      options: [
        { label: "Pending", value: "pending" },
        { label: "Paid", value: "paid" },
        { label: "Waived", value: "waived" },
      ],
    },
    {
      name: "penaltyType",
      title: "Type",
      type: "checkbox",
      options: [
        { label: "Loan Late", value: "loan_late" },
        { label: "Contribution Late", value: "contribution_late" },
      ],
    },
  ];

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-slate-900">Penalty Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">View and manage member penalties</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => processPenaltiesMutation.mutate()}
            disabled={processPenaltiesMutation.isLoading}
            className="gap-1.5"
          >
            {processPenaltiesMutation.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Scale className="h-4 w-4" />
            )}
            Process Penalties
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => (
            <Card key={i} className="animate-pulse"><CardContent className="p-4 h-20 bg-gray-50 rounded-lg" /></Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-amber-700 font-medium">Pending Penalties</p>
                  <p className="text-lg font-bold text-amber-900">
                    {summary?.totalPending?.toLocaleString() || 0} FRW
                  </p>
                  <p className="text-xs text-amber-600">{summary?.pendingCount || 0} pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-green-700 font-medium">Paid Penalties</p>
                  <p className="text-lg font-bold text-green-900">
                    {summary?.totalPaid?.toLocaleString() || 0} FRW
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Ban className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-blue-700 font-medium">Waived Penalties</p>
                  <p className="text-lg font-bold text-blue-900">
                    {summary?.totalWaived?.toLocaleString() || 0} FRW
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-slate-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-700 font-medium">Total Penalties</p>
                  <p className="text-lg font-bold text-slate-900">
                    {summary?.totalAmount?.toLocaleString() || 0} FRW
                  </p>
                  <p className="text-xs text-slate-500">{summary?.overallTotal || 0} records</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px] h-9 text-sm">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="loan_late">Loan Late Repayment</SelectItem>
            <SelectItem value="contribution_late">Contribution Late Payment</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-9 text-sm">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="waived">Waived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={paginatedData}
        facets={facets}
        isLoading={penaltiesLoading}
        defaultColumnVisibility={{}}
        onSearch={handleSearch}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        sorting={sorting}
        setSorting={setSorting}
        setPagination={setPagination}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageCount={pageCount}
        isFetching={false}
      />

      {/* View/Waive/Pay Dialog */}
      <Dialog open={!!selectedPenalty} onOpenChange={(open) => { if (!open) { setSelectedPenalty(null); setWaiveReason(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dialogMode === "view" && <><Eye className="h-4 w-4" /> Penalty Details</>}
              {dialogMode === "waive" && <><Ban className="h-4 w-4 text-blue-600" /> Waive Penalty</>}
              {dialogMode === "pay" && <><CheckCircle2 className="h-4 w-4 text-green-600" /> Mark as Paid</>}
            </DialogTitle>
          </DialogHeader>
          {selectedPenalty && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-gray-500">Member</Label>
                  <p className="font-medium">{selectedPenalty.member?.fullNames || `Member #${selectedPenalty.memberId}`}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Amount</Label>
                  <p className="font-semibold text-base">{Number(selectedPenalty.amount).toLocaleString()} FRW</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Type</Label>
                  <p>{TYPE_LABELS[selectedPenalty.penaltyType] || selectedPenalty.penaltyType}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <Badge variant="outline" className={`${STATUS_STYLES[selectedPenalty.status] || ""} text-xs capitalize`}>
                    {selectedPenalty.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Period</Label>
                  <p>{selectedPenalty.period || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Applied Date</Label>
                  <p>{new Date(selectedPenalty.appliedDate).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="text-sm">
                <Label className="text-xs text-gray-500">Reason</Label>
                <p className="text-gray-700 mt-0.5">{selectedPenalty.reason}</p>
              </div>
              {selectedPenalty.status === "waived" && (
                <div className="text-sm">
                  <Label className="text-xs text-gray-500">Waived By</Label>
                  <p className="text-gray-700 mt-0.5">{selectedPenalty.waivedBy || "N/A"}{selectedPenalty.waivedReason ? ` - Reason: ${selectedPenalty.waivedReason}` : ""}</p>
                </div>
              )}
              {selectedPenalty.status === "paid" && (
                <div className="text-sm">
                  <Label className="text-xs text-gray-500">Paid By</Label>
                  <p className="text-gray-700 mt-0.5">{selectedPenalty.paidBy || "N/A"} {selectedPenalty.paidAt ? `on ${new Date(selectedPenalty.paidAt).toLocaleDateString()}` : ""}</p>
                </div>
              )}
              {dialogMode === "waive" && (
                <div className="space-y-2">
                  <Label className="text-sm">Waive Reason <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={waiveReason}
                    onChange={(e) => setWaiveReason(e.target.value)}
                    placeholder="Enter reason for waiving this penalty..."
                    className="min-h-[80px]"
                  />
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={() => { setSelectedPenalty(null); setWaiveReason(""); }}>
                  {dialogMode === "view" ? "Close" : "Cancel"}
                </Button>
                {dialogMode === "waive" && (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => waiveMutation.mutate({ id: selectedPenalty.id, reason: waiveReason })}
                    disabled={waiveMutation.isLoading || !waiveReason.trim()}
                  >
                    {waiveMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Ban className="h-4 w-4 mr-1" />}
                    Waive Penalty
                  </Button>
                )}
                {dialogMode === "pay" && (
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => payMutation.mutate(selectedPenalty.id)}
                    disabled={payMutation.isLoading}
                  >
                    {payMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                    Confirm Paid
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
